import axios, { AxiosRequestConfig, AxiosResponse } from "axios";

const API_ROOT = import.meta.env.VITE_MARKET_API_ROOT;

export function apiUrl(path: string) {
  return API_ROOT + path.replace(/^\//, "");
}

export function api(axiosOptions: AxiosRequestConfig): Promise<ApiResponse> {
  return handleApiResponse(axios(axiosOptions));
}

const GENERIC_ERROR = "An unexpected error occurred";

export async function handleApiResponse(p: Promise<any>): Promise<ApiResponse> {
  return p
    .then((response: AxiosResponse): ApiResponse => {
      if (response.status >= 200 && response.status < 300) {
        return {
          success: response.data.data?.success ?? true,
          data: response.data.data ?? response.data,
          response: response,
        } as ApiResponse;
      } else {
        return {
          success: false,
          data: {},
          response: response,
          error: new ApiError(GENERIC_ERROR, response.status),
        } as ApiResponse;
      }
    })
    .catch((err: unknown): ApiResponse => {
      // axios rejects on non-2xx and on network failures; normalize both so
      // callers can read a typed `error` instead of catching raw rejections.
      if (!axios.isAxiosError(err)) {
        throw err;
      }

      const response = err.response ?? null;
      const status = response?.status;
      const payload = response?.data;

      return {
        success: false,
        data: {},
        response,
        error: errorFromPayload(payload, status),
      };
    });
}

function errorFromPayload(payload: any, status?: number): ApiError {
  if (status === 422 && isValidationError(payload)) {
    return new ValidationError(payload.message, payload.errors, status);
  }

  const message =
    isObject(payload) && typeof payload.message === "string" && payload.message
      ? payload.message
      : GENERIC_ERROR;

  return new ApiError(message, status);
}

export interface ApiResponse<T = ApiError> {
  success: boolean;
  data: any;
  response: AxiosResponse | null;
  error?: T | ApiError;
}

export class ApiError extends Error {
  status?: number;

  constructor(message: string, status?: number) {
    super(message);
    this.name = "ApiError";
    this.status = status;
  }
}

export class ValidationError extends ApiError {
  errors: ValidationErrors = {};

  constructor(message: string, errors: ValidationErrors, status: number = 422) {
    super(message, status);
    this.name = "ValidationError";
    this.errors = errors;
  }
}

export interface ValidationErrors {
  [field: string]: string[];
}

export const isObject = (thing: any) =>
  thing !== null && typeof thing === "object";

export function isSuccessful(response: AxiosResponse): boolean {
  return response.status >= 200 && response.status < 300;
}

export const isValidationError = (payload: any): payload is ValidationError =>
  isObject(payload) && !!payload.message && !!payload.errors;

export const isApiError = (payload: any): payload is ApiError =>
  isObject(payload) && !(undefined == payload.success) && !!payload.message;
